import { memo, useCallback, useEffect, useState } from "react";
import { LiveClock, ReactiveBackground } from "./Scene";
import CardPortal from "./CardPortal";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Eye, EyeOff, Mail } from "lucide-react";
import { toast } from "sonner";

const SIGNIN_ERROR_RING_KEY = "signin_error_ring";
const SIGNIN_ERROR_RING_MAX = 30;

function pushSigninErrorRing(entry) {
  try {
    let arr = [];
    try {
      const raw = localStorage.getItem(SIGNIN_ERROR_RING_KEY);
      if (typeof raw === "string" && raw.length > 0) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) arr = parsed;
      }
    } catch (_) { /* ignore */ }
    arr.push(entry);
    if (arr.length > SIGNIN_ERROR_RING_MAX) {
      arr = arr.slice(arr.length - SIGNIN_ERROR_RING_MAX);
    }
    localStorage.setItem(SIGNIN_ERROR_RING_KEY, JSON.stringify(arr));
  } catch (_) { /* localStorage full or disabled */ }
}

const NewLoginPage = ({ onLogin, axiosInstance }) => {
  const [showForgotPasswordDialog, setShowForgotPasswordDialog] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [resetStep, setResetStep] = useState(1);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [backendStatus, setBackendStatus] = useState({ ok: null, message: "" });

  // -------------------------------------------------------------------------
  // Proactive sign-in page error monitor.
  //
  // Captures ANY unhandled JavaScript error or unhandled promise rejection
  // that fires in the global window scope while the user is on the sign-in
  // page, and appends it to a bounded ring buffer in localStorage.
  //
  // Purpose: if a user reports "red errors appeared on sign in" but did not
  // have DevTools open at the time, the developer can still pull the ring
  // buffer (paste `JSON.parse(localStorage.getItem('signin_error_ring'))`
  // into the Console) and see exactly what fired, with a full timestamp,
  // message, filename/line, stack, and (for promise rejections) the raw
  // reason string or serialized object.
  //
  // Known sources of "phantom red console lines" this monitor will catch
  // on future regressions:
  //   • React StrictMode double-effect abort of network fetches
  //   • 3rd-party CDN scripts (recaptcha, analytics, font loaders) that
  //     throw on slow networks
  //   • axios / fetch transport errors that propagate to window.onerror
  //     instead of a local .catch() handler
  // -------------------------------------------------------------------------
  useEffect(() => {
    let mounted = true;

    const onError = (event) => {
      if (!mounted) return;
      const entry = {
        t: Date.now(),
        kind: "error",
        message: event?.message ?? null,
        filename: event?.filename ?? null,
        lineno: event?.lineno ?? null,
        colno: event?.colno ?? null,
        stack: event?.error?.stack ?? (event?.error ? String(event.error) : null),
      };
      pushSigninErrorRing(entry);
    };

    const onUnhandledRejection = (event) => {
      if (!mounted) return;
      let reasonStr;
      const rawReason = event?.reason;
      if (rawReason instanceof Error) {
        reasonStr = `${rawReason.name}: ${rawReason.message}\n${rawReason.stack ?? ""}`;
      } else if (typeof rawReason === "object" && rawReason != null) {
        try { reasonStr = JSON.stringify(rawReason); } catch { reasonStr = String(rawReason); }
      } else {
        reasonStr = String(rawReason);
      }
      pushSigninErrorRing({
        t: Date.now(),
        kind: "unhandledrejection",
        reason: reasonStr,
      });
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onUnhandledRejection);

    return () => {
      mounted = false;
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  }, []);

  /**
   * Intentionally no /api/health network check on component mount (initial
   * page load).
   *
   * The prior design ran `axiosInstance.get("/health")` in a useEffect on
   * mount.  Combined with React 18 StrictMode (double-invoke → 2 calls) plus
   * LiveClock's own /api/health check, the sign-in page was issuing THREE
   * HTTP requests to origin BEFORE the user even typed a single character.
   * When the backend process is DOWN (not listening on port), Cloudflare
   * returns HTTP 502 Bad Gateway for every request to origin, and Chrome
   * logs each 502 as a BRIGHT RED network-level line in the Console tab —
   * even when the JavaScript .catch() handler fully swallows the error.
   * The red lines then look like "sign-in page bugs" and users file tickets
   * about them.
   *
   * Permanent, production fix:
   *   • backendStatus starts as `{ ok: null, message: "" }` (unknown state).
   *   • The yellow Sign In button is rendered as "Sign In →" (optimistic,
   *     assuming backend is healthy until proven otherwise).
   *   • Only when the user actually CLICKS Sign In (or submits a
   *     Forgot Password request) do we make a real network call.  If THAT
   *     call fails due to backend being unreachable, we set
   *     backendStatus.ok = false.  On the NEXT render the button switches
   *     to the "🛰 Backend Unavailable" variant, the pink status chip
   *     updates, and the user knows why their submit failed — all without
   *     spamming 3 spurious red 502 console lines on every single page open.
   *   • If backend later recovers, the FIRST successful network call flips
   *     backendStatus.ok back to true (Connected).
   */

  const handleShowForgotPassword = useCallback(() => {
    setShowForgotPasswordDialog(true);
  }, []);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsResettingPassword(true);

    try {
      let response;
      if (resetStep === 1) {
        response = await axiosInstance.post("/auth/forgot-password", {
          email: resetEmail,
        });
      } else {
        if (newPassword !== confirmPassword) {
          toast.error("Passwords do not match");
          setIsResettingPassword(false);
          return;
        }

        if (newPassword.length < 6) {
          toast.error("Password must be at least 6 characters long");
          setIsResettingPassword(false);
          return;
        }

        response = await axiosInstance.post("/auth/forgot-password", {
          email: resetEmail,
          new_password: newPassword,
        });
      }

      // Any 2xx / non-throwing response from forgot-password → backend alive.
      setBackendStatus({ ok: true, message: "Connected" });

      if (resetStep === 1) {
        if (response.data.email_exists) {
          toast.success("Email found! Please enter your new password.");
          setResetStep(2);
        }
      } else {
        toast.success(
          "Password reset successful! You can now login with your new password."
        );
        setShowForgotPasswordDialog(false);
        setResetEmail("");
        setNewPassword("");
        setConfirmPassword("");
        setResetStep(1);
      }
    } catch (error) {
      const status = error?.response?.status;
      const ctRaw = error?.response?.headers?.["content-type"] ??
                   error?.response?.headers?.["Content-Type"] ??
                   null;
      const ct = typeof ctRaw === "string" ? ctRaw.toLowerCase() : "";
      const isJson = ct.includes("application/json");
      const body = error?.response?.data;
      const isBackendDown =
        status == null ||
        status === 502 ||
        status === 503 ||
        status === 504 ||
        (status >= 520 && status <= 530);

      // Severity-aware logging: backend-down events (502 etc.) are routine
      // during restart windows; do NOT paint DevTools console red for them.
      if (isBackendDown) {
        console.info(
          `[sign-in:forgot-pw backend-down] status=${
            status == null ? "transport-failure" : status
          } ct=${ct || "n/a"}. Not an app bug.`,
        );
      } else if (isJson && status !== 500) {
        console.info(
          `[sign-in:forgot-pw validation] status=${status} detail=${
            body?.detail ?? "n/a"
          }`,
        );
      } else {
        console.error("Password reset error:", error);
      }

      if (isBackendDown) {
        const downMsg =
          status === 502
            ? "Origin unreachable (502 Bad Gateway)"
            : status === 503
              ? "Service unavailable (503)"
              : status === 504
                ? "Origin timed out (504 Gateway Timeout)"
                : status == null
                  ? "Backend unreachable"
                  : `Origin error (HTTP ${status})`;
        setBackendStatus({ ok: false, message: downMsg });
      }

      // User-facing toast: never, under any circumstances, render the raw
      // Cloudflare HTML banner body (text/html responses from reverse proxies
      // contain scary jargon users can't act on).
      let userMsg;
      if (isBackendDown) {
        userMsg =
          status === 502
            ? "Backend servers are not responding right now (502 Bad Gateway). Please try again in 30 seconds."
            : status === 503
              ? "Backend is temporarily down for maintenance (503). Please try again shortly."
              : status === 504
                ? "Backend servers timed out (504). Please try again."
                : status == null
                  ? "Cannot reach the backend servers right now. Please check your connection or try again."
                  : "Backend returned an error. Please try again.";
      } else if (isJson && typeof body?.detail === "string" && body.detail.length > 0) {
        userMsg = body.detail;
      } else {
        userMsg = "Failed to reset password. Please try again.";
      }
      toast.error(userMsg);
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleResetDialogClose = () => {
    setShowForgotPasswordDialog(false);
    setResetEmail("");
    setNewPassword("");
    setConfirmPassword("");
    setResetStep(1);
  };

  return (
    <main className="relative flex min-h-dvh w-full flex-col overflow-x-hidden bg-navy-950 [@media(min-height:920px)]:h-dvh [@media(min-height:920px)]:overflow-hidden">
      <ReactiveBackground />

      <header className="relative z-20 flex items-center justify-end gap-4 px-5 py-5 sm:px-10 sm:py-6">
        <LiveClock />
      </header>

      <div className="relative z-10 flex flex-1 items-center justify-center px-4 pt-1 pb-12 sm:px-6">
        <CardPortal
          onLogin={onLogin}
          axiosInstance={axiosInstance}
          backendStatus={backendStatus}
          onBackendStatusChange={setBackendStatus}
          onShowForgotPassword={handleShowForgotPassword}
        />

        <Dialog
          open={showForgotPasswordDialog}
          onOpenChange={handleResetDialogClose}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-indigo-600" />
                Reset Password {resetStep === 2 && " - Step 2"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleForgotPassword} className="space-y-4">
              {resetStep === 1 ? (
                <div className="space-y-2">
                  <Label
                    htmlFor="reset-email"
                    className="text-sm font-medium text-gray-700"
                  >
                    Email Address
                  </Label>
                  <Input
                    id="reset-email"
                    type="email"
                    placeholder="Enter your registered email address"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    className="focus-ring"
                    required
                    disabled={isResettingPassword}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter your email to verify it&apos;s registered in our system
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="text-sm text-gray-600 bg-green-50 p-3 rounded-lg">
                    Email <strong>{resetEmail}</strong> found! Please enter your
                    new password.
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="new-password">New Password</Label>
                    <div className="relative">
                      <Input
                        id="new-password"
                        type={showNewPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Enter your new password"
                        required
                        disabled={isResettingPassword}
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword((v) => !v)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                      >
                        {showNewPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Confirm New Password</Label>
                    <div className="relative">
                      <Input
                        id="confirm-password"
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm your new password"
                        required
                        disabled={isResettingPassword}
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword((v) => !v)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  <p className="text-xs text-gray-500">
                    Password must be at least 6 characters long
                  </p>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleResetDialogClose}
                  className="flex-1"
                  disabled={isResettingPassword}
                >
                  Cancel
                </Button>
                {resetStep === 2 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setResetStep(1)}
                    className="flex-1"
                    disabled={isResettingPassword}
                  >
                    Back
                  </Button>
                )}
                <Button
                  type="submit"
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                  disabled={isResettingPassword}
                >
                  {isResettingPassword ? (
                    <div className="flex items-center gap-2">
                      <div className="loading-spinner" />
                      {resetStep === 1 ? "Checking..." : "Resetting..."}
                    </div>
                  ) : resetStep === 1 ? (
                    "Verify Email"
                  ) : (
                    "Reset Password"
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </main>
  );
};

export default memo(NewLoginPage, function areEqual(prev, next) {
  return (
    prev.onLogin === next.onLogin &&
    prev.axiosInstance === next.axiosInstance
  );
});
